/**
 * Client-side JSONPath scalar extract — mirrors the SQL `extractTextSql` from
 * `jsonFilterSql.ts` so FK navigation on a JSON-pathed loose FK yields the
 * same lookup key as the WHERE clause that `filterWhereBuilder.ts` would emit
 * if the user filtered the source column instead.
 *
 * Supports the subset of JSONPath the rest of Slice 16 declares: `$`,
 * dotted segments (`$.a.b`), bracket-indexed array segments
 * (`$.items[0].name`), and array wildcards (`[*]`). Recursive descent (`..`)
 * is still out of scope — it has no scalar-extract semantic.
 *
 * Two extract shapes are exported:
 *
 *  - `extractJsonPathScalar(value, path)` — scalar-shaped result. Returns
 *    `undefined` if the path doesn't resolve, the document isn't valid JSON,
 *    the path uses a wildcard, or the path resolves to a non-primitive. This
 *    is the shape filter / single-chip callers want.
 *
 *  - `extractJsonPathScalarsAll(value, path)` — array-shaped result. For
 *    non-wildcard paths, behaves like `extractJsonPathScalar` lifted into a
 *    1-element or 0-element array. For wildcard paths, expands each `[*]`
 *    across array elements and returns every primitive that the expansion
 *    reaches. Order is the depth-first walk order. Non-primitive expansions
 *    (objects/arrays at the leaf) are skipped, not surfaced as `undefined`.
 */

import { normalizeJsonPath } from './jsonFilterSql';

type JsonPrimitive = string | number | boolean | null;

type JsonPathSegment =
  | { kind: 'key'; key: string }
  | { kind: 'index'; index: number }
  | { kind: 'wildcard' };

function parsePath(path: string): JsonPathSegment[] {
  const norm = normalizeJsonPath(path);
  const body = norm.replace(/^\$\.?/u, '');
  if (body === '') return [];
  const segments: JsonPathSegment[] = [];
  for (const raw of body.split('.')) {
    if (raw === '') continue;
    // Match `name[N]`, `name[*]`, pure `[N]`/`[*]`, or mixed runs like `a[0][*]`.
    const m = raw.match(/^([^[\]]*)((?:\[(?:\d+|\*)\])*)$/u);
    if (!m) {
      segments.push({ kind: 'key', key: raw });
      continue;
    }
    const [, name, brackets] = m;
    if (name.length > 0) segments.push({ kind: 'key', key: name });
    if (brackets && brackets.length > 0) {
      const found = brackets.match(/\[(\d+|\*)\]/gu) ?? [];
      for (const tok of found) {
        const inner = tok.slice(1, -1);
        if (inner === '*') {
          segments.push({ kind: 'wildcard' });
        } else {
          segments.push({ kind: 'index', index: Number(inner) });
        }
      }
    }
  }
  return segments;
}

function isPrimitive(v: unknown): v is JsonPrimitive {
  return (
    v === null ||
    typeof v === 'string' ||
    typeof v === 'number' ||
    typeof v === 'boolean'
  );
}

function parseRoot(cellValue: unknown): unknown {
  if (cellValue === null || cellValue === undefined) return undefined;
  if (typeof cellValue === 'string') {
    try {
      return JSON.parse(cellValue);
    } catch {
      return undefined;
    }
  }
  return cellValue;
}

function pathHasWildcard(segments: JsonPathSegment[]): boolean {
  return segments.some((s) => s.kind === 'wildcard');
}

/**
 * Walk `segments` over `root` without expanding wildcards — returns `undefined`
 * if any wildcard is encountered (callers should fall through to
 * `walkAllValues`).
 */
function walkScalar(root: unknown, segments: JsonPathSegment[]): unknown {
  let current: unknown = root;
  for (const seg of segments) {
    if (current === null || current === undefined) return undefined;
    if (seg.kind === 'key') {
      if (typeof current !== 'object' || Array.isArray(current)) return undefined;
      current = (current as Record<string, unknown>)[seg.key];
    } else if (seg.kind === 'index') {
      if (!Array.isArray(current)) return undefined;
      current = current[seg.index];
    } else {
      // 'wildcard' — scalar walk can't represent the expansion.
      return undefined;
    }
  }
  return current;
}

/**
 * Walk `segments` over `root`, expanding any `[*]` segments into a depth-first
 * traversal across array elements. Returns every value the expansion reaches
 * (primitives, objects, arrays, or `undefined` for missing keys / out-of-bounds
 * indices — callers filter as needed).
 */
function walkAllValues(root: unknown, segments: JsonPathSegment[]): unknown[] {
  let frontier: unknown[] = [root];
  for (const seg of segments) {
    const next: unknown[] = [];
    for (const cur of frontier) {
      if (cur === null || cur === undefined) continue;
      if (seg.kind === 'key') {
        if (typeof cur !== 'object' || Array.isArray(cur)) continue;
        next.push((cur as Record<string, unknown>)[seg.key]);
      } else if (seg.kind === 'index') {
        if (!Array.isArray(cur)) continue;
        next.push(cur[seg.index]);
      } else {
        if (!Array.isArray(cur)) continue;
        for (const elem of cur) next.push(elem);
      }
    }
    frontier = next;
  }
  return frontier;
}

/**
 * Extract the raw value at `path` (primitive, array, or object) from a cell
 * value. The cell value may be an already-parsed object/array or a
 * stringified JSON document. Returns `undefined` if the path doesn't resolve,
 * the document isn't valid JSON, or the path uses a wildcard (use
 * `extractJsonPathScalarsAll` for the wildcard-expanded shape).
 */
export function extractJsonPathRaw(cellValue: unknown, path: string): unknown {
  const root = parseRoot(cellValue);
  if (root === undefined) return undefined;
  const segments = parsePath(path);
  return walkScalar(root, segments);
}

/**
 * Extract a JSON value at `path` from a cell value. The cell value may be
 * an already-parsed object/array (PG / MySQL native JSON columns) or a
 * stringified JSON document (declared-on-text columns and Oracle / SQLite).
 *
 * Returns the primitive at the path, or `undefined` if the path doesn't
 * exist, the value isn't valid JSON, the path uses a wildcard, or the path
 * resolves to a non-primitive (object/array). Callers should treat
 * `undefined` as "no lookup key".
 */
export function extractJsonPathScalar(
  cellValue: unknown,
  path: string,
): JsonPrimitive | undefined {
  const value = extractJsonPathRaw(cellValue, path);
  return isPrimitive(value) ? value : undefined;
}

/**
 * Extract every primitive value reachable from `path` on `cellValue`.
 *
 * For a path *without* `[*]`, returns `[scalar]` if the path resolves to a
 * primitive, or `[]` otherwise (missing key, non-primitive leaf, invalid JSON).
 *
 * For a path *with* `[*]`, expands the wildcard across each array element at
 * that position and returns every primitive the expansion reaches, in
 * depth-first order. Non-primitive leaves (objects, sub-arrays) are skipped
 * silently rather than surfaced as `undefined`.
 *
 * This is the shape `JsonLooseFkBadges` uses to render one chip per
 * array-element value when a loose FK is declared on a wildcard JSON path.
 */
export function extractJsonPathScalarsAll(
  cellValue: unknown,
  path: string,
): JsonPrimitive[] {
  const root = parseRoot(cellValue);
  if (root === undefined) return [];
  const segments = parsePath(path);
  if (!pathHasWildcard(segments)) {
    const v = walkScalar(root, segments);
    return isPrimitive(v) ? [v] : [];
  }
  const all = walkAllValues(root, segments);
  const out: JsonPrimitive[] = [];
  for (const v of all) {
    if (isPrimitive(v)) out.push(v);
  }
  return out;
}
